(function () {
  'use strict';

  const LAYER_SLIDER_MAP = {
    BG:  'swatchSliderBG',
    C1:  'swatchSliderC1',
    C2:  'swatchSliderC2',
    C3:  'swatchSliderC3',
    C4:  'swatchSliderC4',
    C5:  'swatchSliderC5',
    C6:  'swatchSliderC6',
    C7:  'swatchSliderC7',
    L1:  'swatchSliderL1',
    L2:  'swatchSliderL2',
    MC1: 'swatchSliderMC1',
    MC2: 'swatchSliderMC2',
    MC3: 'swatchSliderMC3',
    MC4: 'swatchSliderMC4',
  };

  const SYSTEM_PROMPT_BASE = `You are an AI design assistant for a vehicle wrap configurator.

Color layers you can control:
- BG     : background / base color
- C1–C7  : accent and detail colors (C1 = primary accent, C7 = secondary)
- L1, L2 : logo colors
- MC1–MC4: model / chassis colors

Finish options (names come from product; match loosely by word):
- gloss, matte, satin, carbon, chrome, etc.

When the user asks you to change something, output ACTION lines in this exact format:
  ACTION: LAYER=<layer> COLOR=<hex>      ← for color changes
  ACTION: FINISH=<finish-name>           ← for finish/overlaminate changes

Rules:
- Always include a short human-readable reply before or after the ACTION lines.
- You CAN see the current colors (provided below). Use them for relative requests like "invert", "darken", "lighten", "swap", or "use the same color as BG".
- Only output ACTION lines for layers explicitly mentioned or clearly implied.
- "background" or "base" → BG.
- "all accent colors" or "accent" → C1 through C7.
- "logo" → L1 and L2.
- "model" or "chassis" → MC1 through MC4.
- Output valid 6-digit hex values (e.g. #1a2b3c).
- For finish, use a single descriptive word that matches the product option (e.g. "matte", "gloss").
- To invert a color, XOR each channel with 0xFF (e.g. #001f5b → #ffe0a4).

Example:
User: Make the background navy and switch to matte finish.
Assistant: I'll set the background to navy and apply matte finish.
ACTION: LAYER=BG COLOR=#001f5b
ACTION: FINISH=matte`;

  let conversationHistory = [];

  function getCurrentStateContext() {
    const sc = window.structuredColors;
    const state = window.state;
    if (!state || !state.colorValues || !sc) return '';

    const lines = ['\n\nCurrent color configuration:'];
    Object.entries(state.colorValues).forEach(function (entry) {
      const layer = entry[0];
      const idx = entry[1];
      const color = sc[idx - 1];
      if (color) {
        lines.push('- ' + layer + ': ' + color.name + ' (' + color.hex + ')');
      }
    });

    if (state.activeOverlaminates && state.activeOverlaminates.size > 0) {
      lines.push('- Active finish: ' + Array.from(state.activeOverlaminates)[0]);
    }

    // Include available palette (name + hex) so AI can suggest real swatches
    if (sc.length) {
      lines.push('\nAvailable color swatches (' + sc.length + ' total, first 40 shown):');
      sc.slice(0, 40).forEach(function (c) {
        lines.push('  ' + c.name + ' ' + c.hex);
      });
      if (sc.length > 40) lines.push('  … and ' + (sc.length - 40) + ' more.');
    }

    return lines.join('\n');
  }

  async function callOpenAI(userMessage) {
    const config = window.VECTARY_CHAT_CONFIG;
    if (!config || !config.apiKey) {
      return 'No API key configured. Add your OpenAI API key in Shopify Admin → Online Store → Themes → Customize → SCS AI Assistant.';
    }

    conversationHistory.push({ role: 'user', content: userMessage });

    const systemPrompt = SYSTEM_PROMPT_BASE + getCurrentStateContext();

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.apiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          ...conversationHistory,
        ],
        temperature: 0.2,
        max_tokens: 600,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const msg = (err.error && err.error.message) || `HTTP ${response.status}`;
      throw new Error(msg);
    }

    const data = await response.json();
    const reply = data.choices[0].message.content;
    conversationHistory.push({ role: 'assistant', content: reply });
    return reply;
  }

  function parseActions(text) {
    const actions = [];
    const lines = text.split('\n');
    for (const line of lines) {
      if (!line.trim().toUpperCase().startsWith('ACTION:')) continue;
      const payload = line.slice(line.indexOf(':') + 1).trim();
      const action = {};
      const kvRe = /(\w+)=(#?[\w-]+)/g;
      let m;
      while ((m = kvRe.exec(payload)) !== null) {
        action[m[1].toUpperCase()] = m[2];
      }
      if (Object.keys(action).length) actions.push(action);
    }
    return actions;
  }

  function applyColorAction(layer, hex) {
    const sliderId = LAYER_SLIDER_MAP[layer.toUpperCase()];
    if (!sliderId) return;
    const slider = document.getElementById(sliderId);
    if (!slider) return;
    if (typeof window.findNearestSwatchIndex !== 'function') return;

    const cleanHex = hex.startsWith('#') ? hex : '#' + hex;
    const index = window.findNearestSwatchIndex(cleanHex);
    slider.value = index;
    if (window.state) window.state.activePreset = null;
    slider.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function applyFinishAction(finishName) {
    const lower = finishName.toLowerCase();
    let target = null;
    document.querySelectorAll('.overlaminate-button').forEach(function (btn) {
      const handle = (btn.getAttribute('data-overlaminate-handle') || '').toLowerCase();
      const text = (btn.textContent || '').toLowerCase();
      if (handle.includes(lower) || text.includes(lower)) target = btn;
    });
    if (target) target.click();
  }

  function applyActions(actions) {
    actions.forEach(function (action) {
      if (action.LAYER && action.COLOR) {
        applyColorAction(action.LAYER, action.COLOR);
      } else if (action.FINISH) {
        applyFinishAction(action.FINISH);
      }
    });
  }

  function stripActions(text) {
    return text
      .split('\n')
      .filter(function (line) { return !line.trim().toUpperCase().startsWith('ACTION:'); })
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  function addMessage(text, role) {
    const messages = document.getElementById('vectary-chat__messages');
    if (!messages) return;
    const div = document.createElement('div');
    div.className = 'vectary-chat__msg vectary-chat__msg--' + role;
    div.textContent = text;
    messages.appendChild(div);
    messages.scrollTop = messages.scrollHeight;
  }

  function setLoading(isLoading) {
    const btn = document.getElementById('vectary-chat__send');
    const input = document.getElementById('vectary-chat__input');
    if (btn) btn.disabled = isLoading;
    if (input) input.disabled = isLoading;

    const existing = document.getElementById('vectary-chat__typing');
    if (isLoading && !existing) {
      const messages = document.getElementById('vectary-chat__messages');
      if (!messages) return;
      const div = document.createElement('div');
      div.id = 'vectary-chat__typing';
      div.className = 'vectary-chat__msg vectary-chat__msg--bot vectary-chat__typing';
      div.textContent = '…';
      messages.appendChild(div);
      messages.scrollTop = messages.scrollHeight;
    } else if (!isLoading && existing) {
      existing.remove();
    }
  }

  async function handleSend() {
    const input = document.getElementById('vectary-chat__input');
    if (!input) return;
    const text = input.value.trim();
    if (!text) return;
    input.value = '';

    addMessage(text, 'user');
    setLoading(true);

    try {
      const reply = await callOpenAI(text);
      const actions = parseActions(reply);
      applyActions(actions);
      addMessage(stripActions(reply), 'bot');
    } catch (e) {
      addMessage('Error: ' + e.message, 'bot');
    } finally {
      setLoading(false);
    }
  }

  function init() {
    const sendBtn = document.getElementById('vectary-chat__send');
    const input = document.getElementById('vectary-chat__input');
    const toggleBtn = document.getElementById('vectary-chat__toggle');
    const body = document.getElementById('vectary-chat__body');

    if (sendBtn) {
      sendBtn.addEventListener('click', handleSend);
    }

    if (input) {
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleSend();
        }
      });
    }

    if (toggleBtn && body) {
      toggleBtn.addEventListener('click', function () {
        body.classList.toggle('hidden');
        toggleBtn.textContent = body.classList.contains('hidden') ? '▼' : '▲';
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
